/** Primitives UI accessibles (WCAG 2.2 AA — Q98) : cibles ≥ 44 px, focus visible. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Inbox, X } from 'lucide-react';
import { t, stateLabel } from '../core/i18n.js';
import { onSyncState, type SyncState } from '../offline/sync.js';
import { Overlay } from './overlay.js';
import { BrandLoader } from './brand-loader.js';
import { Link } from 'react-router-dom';
import { ButtonLink } from './ui/action.js';
import { Banner } from './ui/Feedback.js';
import { CONFLICTS_PATH, SYNC_QUEUE_PATH, useOpenConflicts } from '../features/sync/use-open-conflicts.js';

export function Button(props: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  large?: boolean;
  icon?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** Bouton qui ouvre une fenêtre ou un menu (cloche, choix) : son état est annoncé. */
  ariaHasPopup?: 'dialog' | 'menu';
  ariaExpanded?: boolean;
  /** Rôle du bouton dans SA mise en page (ordre, largeur). Jamais d'apparence. */
  className?: string;
}): ReactNode {
  const cls = [
    'btn',
    props.variant === 'secondary' && 'btn--secondary',
    props.variant === 'danger' && 'btn--danger',
    props.variant === 'ghost' && 'btn--ghost',
    props.large && 'btn--large',
    props.icon && 'btn--icon',
    props.className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type={props.type ?? 'button'}
      className={cls}
      // Sur écran TACTILE, un appui ne donne pas le focus au bouton (contrairement
      // à un clic souris). Quand ce bouton ouvre un dialogue, il n'y avait donc
      // rien à re-focaliser à la fermeture et le focus retombait sur <body> :
      // l'utilisateur au clavier ou au lecteur d'écran était perdu. On le focalise
      // explicitement pour que la restitution du focus fonctionne partout.
      onClick={(e) => {
        e.currentTarget.focus();
        props.onClick?.();
      }}
      disabled={props.disabled}
      aria-label={props.ariaLabel}
      aria-haspopup={props.ariaHasPopup}
      aria-expanded={props.ariaExpanded}
    >
      {props.children}
    </button>
  );
}

export function Field(props: {
  label: string;
  children?: ReactNode;
  error?: string | undefined;
  hint?: string | undefined;
}): ReactNode {
  // Association implicite label/contrôle (WCAG 1.3.1) : le contrôle est DANS le label.
  return (
    <div className="field">
      <label>
        <span className="field-label">{props.label}</span>
        {props.hint ? <span className="muted">{props.hint}</span> : null}
        {props.children}
      </label>
      {props.error ? (
        <span className="field-error" role="alert">
          {props.error}
        </span>
      ) : null}
    </div>
  );
}

export function Input(props: {
  value: string;
  onChange: (value: string) => void;
  /** Valeur quittée : pour les champs qui s'enregistrent en sortant. */
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email';
  ariaLabel?: string;
  spellCheck?: boolean;
  pattern?: string;
  maxLength?: number;
  /** Lecture seule (session d'assistance, droit absent) : la valeur reste lisible. */
  disabled?: boolean;
  /** Nom du champ, lu par les gestionnaires de mots de passe (`username`, `password`, `one-time-code`). */
  name?: string;
  /** Champ présent mais invisible : l'identifiant rappelé aux gestionnaires de mots de passe. */
  hidden?: boolean;
}): ReactNode {
  return (
    <input
      type={props.type ?? 'text'}
      name={props.name}
      hidden={props.hidden}
      readOnly={props.hidden}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      onBlur={props.onBlur}
      placeholder={props.placeholder}
      required={props.required}
      autoComplete={props.autoComplete}
      inputMode={props.inputMode}
      aria-label={props.ariaLabel}
      spellCheck={props.spellCheck}
      pattern={props.pattern}
      maxLength={props.maxLength}
      disabled={props.disabled}
    />
  );
}

/**
 * Onglets segmentés (Phase G) — remplace les rangées de boutons ad hoc.
 * Un seul groupe par écran ; défile horizontalement sur téléphone sans retour
 * à la ligne (classe `.tabs`). `count` affiche ce qu'attend un onglet dans
 * une pastille (rien quand il est vide) : on voit où est le travail sans
 * ouvrir chaque onglet.
 */
export function Tabs<T extends string>(props: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string; count?: number | undefined }[];
  ariaLabel: string;
}): ReactNode {
  const group = useRef<HTMLDivElement>(null);
  const countsKey = props.options.map((option) => `${option.value}:${option.count ?? ''}`).join('|');
  // Une piste qui défile s'estompe du côté où il reste des onglets : on voit qu'il y en a d'autres.
  const [edges, setEdges] = useState({ start: false, end: false });
  useEffect(() => {
    const container = group.current;
    if (!container) return undefined;
    const measure = (): void => {
      const max = container.scrollWidth - container.clientWidth;
      const next = { start: container.scrollLeft > 1, end: container.scrollLeft < max - 1 };
      setEdges((current) => (current.start === next.start && current.end === next.end ? current : next));
    };
    measure();
    container.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(container);
    return () => {
      container.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [countsKey]);
  useEffect(() => {
    const container = group.current;
    const selected = container?.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    if (!container || !selected || container.scrollWidth <= container.clientWidth) return;
    // Move only the horizontal tab strip, never the page or the user's form.
    const left = selected.offsetLeft - container.offsetLeft;
    if (left < container.scrollLeft) container.scrollLeft = left;
    else if (left + selected.offsetWidth > container.scrollLeft + container.clientWidth)
      container.scrollLeft = left + selected.offsetWidth - container.clientWidth;
    // Les pastilles arrivent après le premier rendu et élargissent la piste : l'onglet ouvert reste en vue.
  }, [props.value, countsKey]);
  return (
    <div
      ref={group}
      className={`tabs${edges.start ? ' tabs--more-start' : ''}${edges.end ? ' tabs--more-end' : ''}`}
      role="tablist"
      aria-label={props.ariaLabel}
      onKeyDown={(event) => {
        const buttons = [
          ...(group.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []),
        ];
        const index = buttons.indexOf(event.target as HTMLButtonElement);
        if (index < 0) return;
        let next: number;
        if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') next = (index + buttons.length - 1) % buttons.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = buttons.length - 1;
        else return;
        event.preventDefault();
        buttons[next]?.focus({ preventScroll: true });
        const option = props.options[next];
        if (option) props.onChange(option.value);
      }}
    >
      {props.options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={props.value === o.value}
          tabIndex={props.value === o.value ? 0 : -1}
          onClick={() => props.onChange(o.value)}
        >
          {o.label}
          {o.count ? <>{' '}<span className="tabs__count">{o.count}</span></> : null}
        </button>
      ))}
    </div>
  );
}

const STATE_TONE: Record<string, string> = {
  cloturee: 'ok',
  payee: 'ok',
  accepte: 'ok',
  actif: 'ok',
  conforme: 'ok',
  reussie: 'ok',
  confirme: 'ok',
  valide: 'ok',
  executee: 'ok',
  published: 'ok',
  urgente: 'danger',
  annulee: 'danger',
  refuse: 'danger',
  en_retard: 'danger',
  fuite: 'danger',
  echec_final: 'danger',
  interdit_remise_service: 'danger',
  rejetee: 'danger',
  litigieuse: 'danger',
  suspendu: 'danger',
  sans_abonnement: 'danger',
  en_cours: 'info',
  en_route: 'info',
  envoye: 'info',
  emise: 'info',
  planifiee: 'info',
  soumise: 'info',
  en_pause: 'warn',
  a_qualifier: 'warn',
  a_verifier: 'warn',
  a_approuver: 'warn',
  brouillon: 'warn',
  draft: 'warn',
  retired: 'warn',
  en_attente: 'warn',
  information_requise: 'warn',
  partiellement_payee: 'warn',
  a_renouveler: 'warn',
  grace: 'warn',
  lecture_seule: 'warn',
  expire: 'warn',
  a_reessayer: 'warn',
};

export function StateBadge({ state }: { state: string }): ReactNode {
  const tone = STATE_TONE[state];
  return <span className={`badge${tone ? ` badge--${tone}` : ''}`}>{stateLabel(state)}</span>;
}

export function EmptyState({
  children,
  compact,
}: {
  children?: ReactNode;
  compact?: boolean;
}): ReactNode {
  return (
    <div className={`empty-state${compact ? ' empty-state--compact' : ''}`}>
      {children ?? (
        <>
          <Inbox size={26} aria-hidden="true" />
          <span>{t('commun.aucunResultat')}</span>
        </>
      )}
    </div>
  );
}

/**
 * Loading state of a page or section: the animated logo (`BrandLoader`).
 * Kept under its historical name so every screen shares the one loader.
 */
export function Spinner({ variant = 'page', label }: {
  variant?: 'page' | 'section' | undefined;
  /** What is loading, for screen readers ("Chargement" by default). */
  label?: string | undefined;
}): ReactNode {
  return <BrandLoader variant={variant} label={label} />;
}

/**
 * Indicateur de synchronisation global — jamais de perte silencieuse (§16.2).
 * Dès qu'un envoi attend, échoue ou est refusé, il ouvre « Envois en attente »
 * (WEB1-21) ; qui peut arbitrer ouvre aussi les conflits de l'entreprise.
 */
export function SyncIndicator(): ReactNode {
  const [state, setState] = useState<SyncState>({
    status: 'ok',
    pendingOps: 0,
    pendingMedia: 0,
    refused: 0,
    conflicts: 0,
  });
  const [collapsed, setCollapsed] = useState(false);
  const [animationCycle, setAnimationCycle] = useState(0);
  const previousAttention = useRef(false);
  useEffect(() => onSyncState(setState), []);
  const open = useOpenConflicts();
  // Conflits à arbitrer : ceux de l'entreprise pour qui peut trancher, sinon ceux de cet appareil.
  const arbitration = open.arbitrate ? Math.max(open.total ?? 0, state.conflicts) : 0;
  const pending = state.pendingOps + state.pendingMedia;
  const label =
    state.status === 'offline'
      ? t('commun.horsLigne')
      : state.status === 'error'
        ? t('commun.syncErreur')
        : state.refused > 0
          ? t('commun.syncRefusees', { count: state.refused })
          : pending > 0
            ? t('commun.syncEnAttente', { count: pending })
            : t('commun.syncOk');
  const dot =
    state.status === 'offline'
      ? 'offline'
      : state.status === 'error' || state.refused > 0
        ? 'error'
        : pending > 0
          ? 'pending'
          : 'ok';
  const needsAttention = dot !== 'ok';
  // Quelque chose à voir dans la file de l'appareil : l'indicateur l'ouvre.
  const queueVisible = pending > 0 || state.refused > 0 || state.conflicts > 0 || state.status === 'error';

  useEffect(() => {
    if (needsAttention) {
      setCollapsed(false);
      if (!previousAttention.current) setAnimationCycle((cycle) => cycle + 1);
      previousAttention.current = true;
      return undefined;
    }

    // À l'arrivée comme après une reprise réseau, rendre la confirmation
    // perceptible avant de ne garder que le point vert compact.
    setCollapsed(false);
    setAnimationCycle((cycle) => cycle + 1);
    previousAttention.current = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = window.setTimeout(() => setCollapsed(true), reducedMotion ? 250 : 1_650);
    return () => window.clearTimeout(timer);
  }, [needsAttention]);

  const indicator = (
    <span
      className={`sync-indicator sync-indicator--${dot}${collapsed ? ' is-collapsed' : ''}${arbitration ? ' has-conflicts-link' : ''}`}
      role="status"
      aria-live="polite"
      title={label}
    >
      <span className="sr-only">{label}</span>
      {/* Le point est l'ORIGINE : le libellé se déroule hors de lui et y est
          réaspiré. La colonne du libellé passe de `1fr` à `0fr`, ce qui rogne
          le texte depuis sa droite pendant qu'il glisse vers le point — le
          texte ne disparaît donc plus indépendamment du mouvement. */}
      <span className="sync-indicator__track" aria-hidden="true">
        <span key={`${dot}:${animationCycle}`} className={`sync-dot sync-dot--${dot}`} />
        <span className="sync-label">
          <span className="sync-label__text">{label}</span>
        </span>
      </span>
      {state.conflicts > 0 && !arbitration ? (
        <span className="badge badge--warn">
          {state.conflicts} {state.conflicts === 1 ? 'conflit' : 'conflits'}
        </span>
      ) : null}
    </span>
  );
  const opened = queueVisible ? (
    <Link className="sync-queue-link" to={SYNC_QUEUE_PATH} aria-label={`${label} — ${t('commun.syncOuvrir')}`}>
      {indicator}
    </Link>
  ) : indicator;
  // Qui peut trancher ouvre les conflits depuis l'indicateur (refonte v4, 18/10/2026).
  if (!arbitration) return opened;
  return (
    <>
      {opened}
      <Link
        className="sync-conflicts-link"
        to={CONFLICTS_PATH}
        aria-label={t('conflits.lienIndicateur', { n: arbitration })}
      >
        <span className="badge badge--warn" aria-hidden="true">
          {arbitration} {arbitration === 1 ? 'conflit' : 'conflits'}
        </span>
      </Link>
    </>
  );
}

/**
 * Modale — rendue par PORTAL hors des contextes de superposition (voir
 * `overlay.tsx`). Avant, elle était posée là où le composant vivait : dans la
 * topbar (scanner QR), elle restait sous la bottom-nav et ses clics étaient
 * interceptés. `Overlay` fournit aussi Échap, focus initial/restitué et
 * blocage du défilement d'arrière-plan.
 */
export function Modal(props: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** `large` pour un formulaire long : plus large sur ordinateur, plein écran sur téléphone. */
  size?: 'standard' | 'large';
}): ReactNode {
  if (!props.open) return null;
  return (
    <Overlay label={props.title} onClose={props.onClose} variant="modale" size={props.size ?? 'standard'}>
      <div className="modal-panel__header">
        <h2>{props.title}</h2>
        <Button variant="ghost" onClick={props.onClose} ariaLabel={t('commun.fermer')}>
          <X size={18} aria-hidden="true" />
        </Button>
      </div>
      {props.children}
    </Overlay>
  );
}

/** Confirmation commune aux actions irréversibles ou à effets secondaires. */
export function ConfirmActionModal(props: {
  open: boolean;
  title: string;
  consequence: ReactNode;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  pending?: boolean;
  error?: string | undefined;
  tone?: 'primary' | 'danger';
  reason?: { label: string; required?: boolean; minLength?: number; placeholder?: string };
  confirmation?: { phrase: string; label?: string } | undefined;
  /**
   * Le geste ne peut pas aboutir (réglage manquant…) : la confirmation
   * disparaît pour ne pas rester l'action principale ; `action` (« Régler
   * maintenant ») la remplace quand le compte peut lever le blocage.
   */
  blocked?: { action?: { label: string; to: string } | undefined } | undefined;
}): ReactNode {
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  useEffect(() => {
    if (!props.open) {
      setReason('');
      setConfirmation('');
    }
  }, [props.open]);
  const minLength = props.reason?.minLength ?? 3;
  const reasonValid = !props.reason?.required || reason.trim().length >= minLength;
  const confirmationValid =
    !props.confirmation || confirmation.trim() === props.confirmation.phrase;
  return (
    // ui-allow ui/no-form-in-modal: confirmation d'un geste irréversible, le motif ou le nom recopié en fait partie (UI.md : la modale sert aux confirmations)
    <Modal
      open={props.open}
      title={props.title}
      onClose={props.pending ? () => undefined : props.onClose}
    >
      <div className="stack confirm-action">
        <div className="confirm-action__consequence">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>{props.consequence}</div>
        </div>
        {props.reason ? (
          <Field
            label={props.reason.label}
            error={
              !reasonValid && reason.length > 0 ? `${minLength} caractères minimum.` : undefined
            }
          >
            <textarea
              rows={3}
              value={reason}
              maxLength={500}
              placeholder={props.reason.placeholder}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        ) : null}
        {props.confirmation ? (
          <Field
            label={
              props.confirmation.label ?? `Saisissez ${props.confirmation.phrase} pour confirmer`
            }
          >
            <Input
              value={confirmation}
              onChange={setConfirmation}
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        ) : null}
        {props.error ? <Banner tone="danger">{props.error}</Banner> : null}
        <div className="row confirm-action__actions">
          <Button variant="secondary" disabled={Boolean(props.pending)} onClick={props.onClose}>
            {props.blocked ? t('commun.fermer') : t('commun.annuler')}
          </Button>
          {props.blocked ? (
            props.blocked.action ? <ButtonLink to={props.blocked.action.to}>{props.blocked.action.label}</ButtonLink> : null
          ) : (
            <Button
              variant={props.tone === 'danger' ? 'danger' : 'primary'}
              disabled={props.pending || !reasonValid || !confirmationValid}
              onClick={() => props.onConfirm(reason.trim() || undefined)}
            >
              {props.pending ? 'Traitement…' : props.confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export { PageHeader } from './ui/PageHeader.js';
